<script setup lang="ts">
/**
 * Trace edit FABs — top-right of the canvas (brief §5.3).
 *
 * Draw: a single Cancel to abort the in-progress shape.
 * Edit: Edit points/handles toggle · Flip handle type (only with a node
 * selected in handle mode) · Cancel (discard layer) · OK (commit to stack).
 * A non-blocking toast on entry points the user at the mode toggle.
 */
import {
  IconArrowBackUp,
  IconArrowsShuffle,
  IconCheck,
  IconPointer,
  IconVectorBezier,
  IconX,
} from '@tabler/icons-vue';
import { useLayersStore } from '@/stores/layers';
import { useTraceEditor } from '@/composables/useTraceEditor';

const layers = useLayersStore();
const trace = useTraceEditor();
</script>

<template>
  <div
    v-if="layers.activeLayer === 'trace' && layers.editingTraceLayerId"
    class="trace-fabs"
  >
    <div v-if="trace.showEditToast.value && trace.mode.value === 'edit'" class="toast">
      Tap to move the outline points
      <span class="arrow">→</span>
    </div>

    <div class="fab-col">
      <!-- §7: step-undo while drawing — removes the last tap's vertex(es).
           One tap = one undo step, even when it snap-placed several at once. -->
      <button
        v-if="trace.mode.value === 'draw' && trace.canUndoStep.value"
        class="fab"
        title="Undo last point"
        @click="trace.undoStep()"
      >
        <IconArrowBackUp :size="20" stroke-width="1.75" />
      </button>

      <template v-if="trace.mode.value === 'edit'">
        <button
          class="fab toggle"
          :title="trace.subMode.value === 'handles' ? 'Edit points' : 'Edit handles'"
          @click="trace.toggleSubMode()"
        >
          <IconPointer v-if="trace.subMode.value === 'handles'" :size="20" stroke-width="1.75" />
          <IconVectorBezier v-else :size="20" stroke-width="1.75" />
        </button>

        <button
          v-if="trace.canFlip.value"
          class="fab"
          title="Flip handle type (smooth / cusp)"
          @click="trace.flipSelected()"
        >
          <IconArrowsShuffle :size="20" stroke-width="1.75" />
        </button>
      </template>

      <button class="fab cancel" title="Discard this trace" @click="trace.cancel()">
        <IconX :size="20" stroke-width="1.75" />
      </button>

      <button
        v-if="trace.mode.value === 'edit'"
        class="fab ok"
        title="Keep this trace"
        @click="trace.commit()"
      >
        <IconCheck :size="20" stroke-width="1.75" />
      </button>
    </div>
  </div>
</template>

<style scoped>
.trace-fabs {
  position: absolute;
  top: 12px;
  right: 12px;
  display: flex;
  align-items: flex-start;
  gap: 10px;
  z-index: 5;
  pointer-events: none;
}

.fab-col {
  display: flex;
  flex-direction: column;
  gap: 8px;
  pointer-events: auto;
}

.fab {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border-radius: 50%;
  border: 1px solid var(--ui-border);
  background: var(--ui-surface);
  color: var(--text-primary);
  cursor: pointer;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  transition:
    border-color 0.12s,
    background 0.12s;
}

.fab:hover {
  border-color: var(--accent);
  background: color-mix(in srgb, var(--accent) 12%, var(--ui-surface));
}

.fab.toggle {
  background: color-mix(in srgb, var(--accent) 18%, var(--ui-surface));
  border-color: var(--accent);
}

.fab.ok {
  border-color: var(--accent);
  color: var(--accent);
}

.fab.cancel:hover {
  border-color: var(--danger);
  color: var(--danger);
  background: color-mix(in srgb, var(--danger) 12%, var(--ui-surface));
}

.toast {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 4px;
  padding: 8px 12px;
  font-size: 12.5px;
  color: var(--text-primary);
  background: var(--ui-surface);
  border: 1px solid var(--ui-border);
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.18);
  pointer-events: auto;
  max-width: 200px;
}

.toast .arrow {
  color: var(--accent);
  font-weight: 700;
}
</style>
